import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

import { ZfBox, ZfDivider, ZfIcon, ZfLink, ZfPopover, ZfStack, ZfTypography } from '@zafire/react';
import { GlobalState } from 'reducers/stateTypes';
import ContactNote from 'types/ContactNote';
import { formatBrLabel } from 'utils/contactNoteUtil';
import { EnvConfig } from 'config/envConfig';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';

import BRClientRepresentativeTableView from './BRClientRepresentativeTableView';

const AttendeeBRView = () => {
  const { t } = useTranslation();
  const contactNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);
  const usedInBr = useSelector(selectIsUsedInBr);

  const showClientRepTable = useMemo(() => {
    return EnvConfig.config.enableBrClientRepresentativeTable && usedInBr;
  }, [usedInBr, EnvConfig.config.enableBrClientRepresentativeTable]);

  const additionalAttendeesLabel = useMemo(() => {
    if (contactNote?.additionalAttendees?.length === 0) {
      return '';
    }
    return (
      contactNote?.additionalAttendees
        .map((item) => {
          if (item.fullName) {
            return `${item.fullName} (${item.userId.toLowerCase()})`;
          }

          const employeeName = employeeOptions.find((co) => co.value === item.userId.toLowerCase());
          if (employeeName) {
            return employeeName.label;
          }
          return `N/A (${item.userId})`;
        })
        .join(', ') || '-'
    );
  }, [employeeOptions, contactNote?.additionalAttendees]);

  const handleClmLinkClick = (event: React.MouseEvent, message: { message: string; value: object }) => {
    const isInsideIframe = window.parent !== window;
    const clmUrl = EnvConfig.config.hostUrl;
    if (!isInsideIframe || !clmUrl) return;

    event.preventDefault();
    window.parent.postMessage(message, EnvConfig.config.hostUrl);
  };

  return (
    <>
      <ZfStack direction="row" spacing="small">
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('contactNote.form.attendeeAndBR')}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-sm)' }} />
      </ZfStack>
      <ZfStack spacing="extra-extra-small">
        <ZfStack spacing="extra-extra-small" direction="row">
          <ZfTypography color="normal">{t('contactNote.form.clientRepresentative')}</ZfTypography>
          <ZfPopover>
            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
            <ZfBox styles={{ textWrap: 'wrap' }}>
              <ZfTypography variant="label-tiny" color="normal">
                {t('contactNote.form.clientRepresentativePopover')}
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfStack>
        {showClientRepTable && contactNote?.relatedPartners && <BRClientRepresentativeTableView />}
        {EnvConfig.config.enableBrClientRepresentativeTable ? (
          <>
            {!usedInBr && contactNote?.relatedPartners?.length === 0 && <ZfTypography color="emphasis">-</ZfTypography>}
            {!usedInBr && (contactNote?.relatedPartners?.length ?? 0) > 0 && (
              <ZfStack direction="row" spacing="extra-small" style={{ flexWrap: 'wrap' }}>
                {' '}
                {contactNote?.relatedPartners.map((item, index) => (
                  <ZfBox key={`${index}_${item.globalPartnerId}_${item.partnerUId}`}>
                    <ZfLink
                      type="inline"
                      target="_blank"
                      rel="noreferrer"
                      key={`${index}_${item.globalPartnerId}_${item.partnerUId}`}
                      href={`${EnvConfig.config.hostUrl}/partner/edit/${item.partnerUId}`}
                      onClick={(e) =>
                        handleClmLinkClick(e, { message: 'redirectToPartner', value: { partnerUid: item.partnerUId } })
                      }
                    >{`${item.fullName} (${item.globalPartnerId})`}</ZfLink>
                    {index < (contactNote?.relatedPartners?.length ?? 0) - 1 ? ', ' : ''}
                  </ZfBox>
                ))}
              </ZfStack>
            )}
          </>
        ) : (
          <>
            {contactNote?.relatedPartners?.length === 0 && <ZfTypography color="emphasis">-</ZfTypography>}
            {(contactNote?.relatedPartners?.length ?? 0) > 0 && (
              <ZfStack direction="row" spacing="extra-small" style={{ flexWrap: 'wrap' }}>
                {' '}
                {contactNote?.relatedPartners.map((item, index) => (
                  <ZfBox key={`${index}_${item.globalPartnerId}_${item.partnerUId}`}>
                    <ZfLink
                      type="inline"
                      target="_blank"
                      rel="noreferrer"
                      key={`${index}_${item.globalPartnerId}_${item.partnerUId}`}
                      href={`${EnvConfig.config.hostUrl}/partner/edit/${item.partnerUId}`}
                      onClick={(e) =>
                        handleClmLinkClick(e, { message: 'redirectToPartner', value: { partnerUid: item.partnerUId } })
                      }
                    >{`${item.fullName} (${item.globalPartnerId})`}</ZfLink>
                    {index < (contactNote?.relatedPartners?.length ?? 0) - 1 ? ', ' : ''}
                  </ZfBox>
                ))}
              </ZfStack>
            )}
          </>
        )}
      </ZfStack>
      {contactNote?.otherPartner && (
        <ZfStack spacing="extra-extra-small" style={{ overflowWrap: 'anywhere' }}>
          <ZfTypography color="normal">{t('contactNote.form.otherPartner')}</ZfTypography>
          <ZfTypography color="emphasis">{contactNote.otherPartner}</ZfTypography>{' '}
        </ZfStack>
      )}
      <ZfStack spacing="extra-extra-small">
        <ZfStack spacing="extra-extra-small" direction="row">
          <ZfTypography color="normal">{t('contactNote.form.relatedBRs')}</ZfTypography>
          <ZfPopover>
            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
            <ZfBox>
              <ZfTypography variant="label-tiny" color="normal">
                {t('contactNote.form.relatedBRsPopover')}
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfStack>
        {contactNote?.relatedBRs.length === 0 && <ZfTypography color="emphasis">-</ZfTypography>}
        {(contactNote?.relatedBRs.length ?? 0) > 0 && (
          <ZfStack direction="row" spacing="extra-small" style={{ flexWrap: 'wrap' }}>
            {contactNote?.relatedBRs.map((item, index) => (
              <ZfBox key={`${index}_${item.masterNumber}_${item.relatedBrUid}`} styles={{ flexDirection: 'row' }}>
                {item.masterNumber ? (
                  <ZfLink
                    type="inline"
                    target="_blank"
                    rel="noreferrer"
                    key={`${index}_${item.masterNumber}_${item.relatedBrUid}`}
                    href={`${EnvConfig.config.hostUrl}/business-relation/edit/${item.relatedBrUid}`}
                    onClick={(e) =>
                      handleClmLinkClick(e, { message: 'newTabBrCoreData', value: { brId: item.relatedBrUid } })
                    }
                  >
                    {formatBrLabel(item.masterNumber, item.brName)}
                  </ZfLink>
                ) : (
                  <ZfTypography color="emphasis">{t('contactNote.form.prospectingBR')}</ZfTypography>
                )}
                {index < (contactNote?.relatedBRs?.length ?? 0) - 1 ? ', ' : ''}
              </ZfBox>
            ))}
          </ZfStack>
        )}
      </ZfStack>
      <ZfStack spacing="extra-extra-small">
        <ZfTypography color="normal">{t('contactNote.form.bankRepresentative')}</ZfTypography>
        <ZfTypography color="emphasis">{additionalAttendeesLabel}</ZfTypography>
      </ZfStack>
    </>
  );
};

export default AttendeeBRView;
